import React, { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import Select from 'components/form/Select';
import Button from 'components/Button';
import Radio from 'components/form/Radio';
import Timepicker from 'components/form/TimePicker';
import Preview from '../previews/Scheduler';
import { shortId, toUserTimezone } from 'utils';
import IconButton from 'components/IconButton';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import TextField from '@mui/material/TextField';
import weekdays from 'constants/weekdays';

const WEEKDAYS = weekdays.map((day) => ({ label: day, value: day }));

const SchedulerQuestion = ({ onChange, values }) => {
  const [t] = useTranslation('connectCard');
  const { question_possible_answers: options, question_settings } = values;
  const { type } = question_settings;

  useEffect(() => {
    if (!options?.length) {
      const choice = {
        id: shortId(),
      };
      options.push(choice);
      if (!type) {
        choice.dow = '';
        values.question_settings.type = 'recurring';
      } else {
        values.question_settings.type = 'fixed';
      }
      onChange(values);
    }
  }, []);

  const onAddOption = () => {
    options.push({ id: shortId() });
    onChange(values);
  };

  const onRemove = (option) => {
    options.splice(
      options.findIndex((o) => o.id === option.id),
      1,
    );
    onChange(values);
  };

  const onDOWChange = (option, value) => {
    const index = options.findIndex((o) => o.id === option.id);
    options[index].dow = value;
    onChange(values);
  };

  const onTimeChange = (option, value) => {
    const index = options.findIndex((o) => o.id === option.id);
    options[index].time = value;
    onChange(values);
  };

  const onDateChange = (option, value) => {
    const index = options.findIndex((o) => o.id === option.id);
    options[index].date = value.format('YYYY-MM-DD HH:mm:ss.SS');
    onChange(values);
  };

  const onTypeChange = (e) => {
    const { value } = e.target;
    values.question_settings.type = value;
    options.length = 0;
    onAddOption();
  };

  return (
    <Stack>
      <Radio
        name="type"
        value="recurring"
        checked={type === 'recurring'}
        onChange={onTypeChange}
        label={t(
          'schedule-choice-recurring-label',
          'Weekly Worship (Most Common)',
        )}
        hint={t(
          'schedule-choice-recurring-hint',
          'Select a day of the week and time for your primary worship experience.',
        )}
      />
      {type === 'recurring' && (
        <Box>
          {options?.map((choice, index) => (
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              key={choice.id}
            >
              <Select
                transformValue
                margin="dense"
                menuPosition="fixed"
                size="small"
                options={WEEKDAYS}
                value={choice.dow}
                onChange={(v) => onDOWChange(choice, v)}
              />
              <Typography>{t('commmon:at', 'at')}</Typography>
              <Timepicker
                value={choice.time}
                onChange={(v) => onTimeChange(choice, v)}
              />
              {options.length > 1 && (
                <IconButton
                  edge="end"
                  color="secondary"
                  icon={<DeleteIcon />}
                  onClick={() => onRemove(choice)}
                />
              )}
            </Stack>
          ))}
          <Box mt={1} mb={1}>
            <Button
              startIcon={<AddIcon />}
              variant="text"
              onClick={onAddOption}
            >
              {t('add-another-time', 'Add another time')}
            </Button>
          </Box>
        </Box>
      )}
      <Radio
        name="type"
        value="fixed"
        checked={type === 'fixed'}
        onChange={onTypeChange}
        label={t('schedule-choice-fixed-label', 'Preset Event Dates & Times')}
        hint={t(
          'schedule-choice-fixed-hint',
          'Offer a choice of one or more event times. Example: Christmas Eve at 9am, Chistmas Eve at 11am, Christmas Eve at 11pm',
        )}
      />
      {type === 'fixed' && (
        <Stack mt={1}>
          {options?.map((choice) => (
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              key={choice.id}
            >
              <DateTimePicker
                disablePast
                value={choice.date || null}
                onChange={(v) => onDateChange(choice, v)}
                inputFormat="LLL"
                renderInput={(props) => (
                  <TextField
                    {...props}
                    inputProps={{
                      ...props.inputProps,
                      placeholder: t(
                        'select-date-and-time',
                        'Select Date and time',
                      ),
                    }}
                    margin="none"
                  />
                )}
              />
              {options.length > 1 && (
                <IconButton
                  edge="end"
                  color="secondary"
                  icon={<DeleteIcon />}
                  onClick={() => onRemove(choice)}
                />
              )}
            </Stack>
          ))}
          <Box mt={1} mb={1}>
            <Button
              startIcon={<AddIcon />}
              variant="text"
              onClick={onAddOption}
            >
              {t('add-another-time', 'Add another time')}
            </Button>
          </Box>
        </Stack>
      )}
    </Stack>
  );
};

SchedulerQuestion.StepsCount = 3;
SchedulerQuestion.Preview = Preview;
SchedulerQuestion.StepDescription =
  'Edit the date & time fields for this question';

export default SchedulerQuestion;
